import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Loader2, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const DataTable = ({
  title,
  description,
  data = [],
  columns = [],
  formFields = [],
  onAdd,
  onEdit,
  onDelete,
  loading = false,
  searchable = true,
  searchField = 'name',
  customActions = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const filteredData = data.filter((item) =>
    String(item[searchField] || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item });
    } else {
      setEditingItem(null);
      const initialData = {};
      formFields.forEach((field) => {
        initialData[field.name] = field.defaultValue || '';
      });
      setFormData(initialData);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingItem(null);
    setFormData({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingItem) {
        await onEdit(editingItem.id, formData);
        toast.success('Updated successfully');
      } else {
        await onAdd(formData);
        toast.success('Created successfully');
      }
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await onDelete(id);
        toast.success('Deleted successfully');
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const renderFormField = (field) => {
    const value = formData[field.name] ?? '';

    if (field.type === 'select') {
      return (
        <Select
          value={String(value)}
          onValueChange={(val) => setFormData({ ...formData, [field.name]: val })}
        >
          <SelectTrigger className="h-10">
            <SelectValue placeholder={`Select ${field.label}`} />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (field.type === 'textarea') {
      return (
        <Textarea
          value={value}
          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          placeholder={field.placeholder}
          className="min-h-[80px]"
        />
      );
    }

    return (
      <Input
        type={field.type || 'text'}
        value={value}
        onChange={(e) => {
          const val = field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
          setFormData({ ...formData, [field.name]: val });
        }}
        placeholder={field.placeholder}
        className="h-10"
        required={field.required}
      />
    );
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'bg-emerald-100 text-emerald-700',
      inactive: 'bg-slate-100 text-slate-600',
      new: 'bg-blue-100 text-blue-700',
      qualified: 'bg-teal-100 text-teal-700',
      contacted: 'bg-amber-100 text-amber-700',
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      draft: 'bg-slate-100 text-slate-600',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-emerald-100 text-emerald-700',
      overdue: 'bg-red-100 text-red-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700',
      planning: 'bg-purple-100 text-purple-700',
      on_hold: 'bg-amber-100 text-amber-700',
      todo: 'bg-slate-100 text-slate-600',
      open: 'bg-blue-100 text-blue-700',
      closed: 'bg-slate-100 text-slate-600',
      qualification: 'bg-slate-100 text-slate-600',
      proposal: 'bg-blue-100 text-blue-700',
      negotiation: 'bg-amber-100 text-amber-700',
      closed_won: 'bg-emerald-100 text-emerald-700',
      closed_lost: 'bg-red-100 text-red-700',
      present: 'bg-emerald-100 text-emerald-700',
      absent: 'bg-red-100 text-red-700',
      late: 'bg-amber-100 text-amber-700',
    };

    const colorClass = statusColors[status?.toLowerCase()] || 'bg-slate-100 text-slate-600';
    return (
      <Badge className={`${colorClass} font-medium capitalize`}>
        {status?.replace(/_/g, ' ') || 'N/A'}
      </Badge>
    );
  };

  const renderCellValue = (item, column) => {
    const value = item[column.key];

    if (column.render) {
      return column.render(value, item);
    }

    if (column.type === 'status') {
      return getStatusBadge(value);
    }

    if (column.type === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value || 0);
    }

    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString() : '-';
    }

    if (column.type === 'percentage') {
      return `${value || 0}%`;
    }

    return value || '-';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{title}</h1>
          {description && <p className="text-slate-500 mt-1">{description}</p>}
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-teal-700 hover:bg-teal-800"
          data-testid={`add-${title.toLowerCase().replace(/\s/g, '-')}-btn`}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New
        </Button>
      </div>

      {/* Search */}
      {searchable && (
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10 border-slate-300"
            data-testid="search-input"
          />
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {columns.map((column) => (
                    <TableHead
                      key={column.key}
                      className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
                    >
                      {column.label}
                    </TableHead>
                  ))}
                  <TableHead className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((item) => (
                    <TableRow key={item.id} className="hover:bg-slate-50/50" data-testid={`row-${item.id}`}>
                      {columns.map((column) => (
                        <TableCell key={column.key} className="text-sm text-slate-700">
                          {renderCellValue(item, column)}
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(item)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(item.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                {(customActions || []).map((action, index) => {
                  const isHidden = typeof action.hidden === "function" ? action.hidden(item) : action.hidden;
                  return isHidden ? null : (
                    <DropdownMenuItem key={index} onClick={() => action.onClick(item)}>
                      {action.label}
                    </DropdownMenuItem>
                  );
                })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} className="h-32 text-center text-slate-500">
                      No data found. Click "Add New" to create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem ? 'Edit' : 'Add New'} {title.replace(/s$/, '')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {formFields.map((field) => (
                <div key={field.name} className="grid gap-2">
                  <Label htmlFor={field.name} className="text-slate-700">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {renderFormField(field)}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-teal-700 hover:bg-teal-800"
                disabled={submitting}
                data-testid="submit-form-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataTable;
